import React, { FC, useState } from 'react'
import { Container, Autolocalize, UserInteraction } from '@libs/components'
import { Col, message, Row, Typography, Spin, Button } from 'antd'
import { Formik } from 'formik'
import { AutoComplete, Form, FormItem, Select } from 'formik-antd'
import { useRouter } from 'next/router'
import { useMutation, useQueryClient } from 'react-query'
import { AuthApi, HEADER_USER_ID_KEY, IBranch, StorageHeaderValueType, UserAccess } from '@libs/api'
import appRoutes from 'app.routes'
import { useBranchUsersList, useBranchSearch } from './hooks'
import Styled from './Styled'
import { useDebounce } from 'use-debounce/lib'
import QUERIES from 'modules/common/queries'
import { useLocalization } from '@libs/localization'
import { createValidationSchema } from '@libs/util'
import { useAuth } from '../hooks'
import { AUTH_REDIRECT_URL } from '@libs/api/build/http'

export interface IFormValues {
	departmentName?: number
	customerId?: string
}

export const formSchema = createValidationSchema(yup =>
	yup.object().shape({
		departmentName: yup.string().required(),
		customerId: yup.string().required(),
	})
)

const initialFormValues: IFormValues = {
	departmentName: undefined,
	customerId: undefined,
}

const ActiveDirectoryPageContent: FC = () => {
	const { t, updateLocale } = useLocalization()
	const router = useRouter()
	const queryClient = useQueryClient()
	const { logout } = useAuth()

	const [queryString, setQueryString] = useState('')
	const [selectedDepartmentId, setSelectedDepartmentId] = useState<IBranch['id']>()

	const { data: departments, isLoading: areDepartmentsLoading } = useBranchSearch(queryString)
	const { data: departmentMembers = [], isLoading: areDepartmentMembersLoading } =
		useBranchUsersList(selectedDepartmentId)

	const [areDepartmentsStillLoading] = useDebounce(areDepartmentsLoading, 500)
	const [areDepartmentMembersStillLoading] = useDebounce(areDepartmentMembersLoading, 500)

	const { mutateAsync, isLoading } = useMutation(
		async ({ customerId }: IFormValues) => {
			try {
				// update header value to perform actions for the selected user
				const userIdHeader: StorageHeaderValueType = JSON.parse(localStorage.getItem(HEADER_USER_ID_KEY) || '')
				userIdHeader.value = customerId
				localStorage.setItem(HEADER_USER_ID_KEY, JSON.stringify(userIdHeader))
				const user = await AuthApi.getAuthenticatedUser()
				if (user?.branches?.[0]?.countryCode === 'SE') {
					updateLocale('sv')
				}
				const redirectUrl = localStorage.getItem(AUTH_REDIRECT_URL)
				localStorage.removeItem(AUTH_REDIRECT_URL)
				await router.push(redirectUrl || appRoutes.dashboard())
			} catch (err) {
				message.error((err as Error).message)
			}
		},
		{
			onError: (err: Error) => {
				message.error(err.message)
			},
			onSuccess: () => {
				queryClient.invalidateQueries(QUERIES.user)
			},
		}
	)

	return (
		<Row>
			<Col xs={24} lg={{ span: 16, push: 4 }} xl={{ span: 12, push: 6 }}>
				<Container margin={{ bottom: 'xxl' }}>
					<Typography.Title level={2}>{t('auth.pages.activeDirectory.title')}</Typography.Title>
					<Typography.Paragraph>{t('auth.pages.activeDirectory.instructions')}</Typography.Paragraph>
				</Container>
				<Formik<IFormValues>
					initialValues={initialFormValues}
					validationSchema={formSchema}
					onSubmit={values => mutateAsync(values)}
				>
					{({ dirty, isValid, setFieldValue, isSubmitting }) => (
						<Form layout="vertical">
							<Autolocalize />
							<UserInteraction enabled={!isSubmitting}>
								<FormItem name="department" label={t('common.terms.department')} required>
									<AutoComplete
										name="departmentName"
										autoFocus
										placeholder={t('common.placeholders.minChars', { min: 3 })}
										notFoundContent={
											areDepartmentsStillLoading ? (
												<Container display={'flex'} justifyContent={'center'}>
													<Spin />
												</Container>
											) : undefined
										}
										onSearch={value => setQueryString(value)}
										onSelect={value => {
											setFieldValue('customerId', undefined)
											setSelectedDepartmentId(
												departments.find(candidate => `${candidate.name} (${candidate.axCustomerId})` === value)?.id
											)
										}}
										showSearch
										filterOption={false}
										options={departments.map(({ name, axCustomerId }) => {
											const formattedValue = `${name} (${axCustomerId})`

											return {
												label: formattedValue,
												value: formattedValue,
											}
										})}
									/>
								</FormItem>
								<FormItem name="customerId" label={t('common.terms.user')} required>
									<Select
										showSearch
										name="customerId"
										placeholder={t('common.placeholders.selectUser')}
										loading={areDepartmentMembersStillLoading}
										disabled={!selectedDepartmentId}
										filterOption={(inputValue, option) => {
											const formattedKey = inputValue.trim().toLocaleLowerCase()
											const foundOption = departmentMembers.find(({ id }) => id === option?.value)
											if (formattedKey.length === 0 || !foundOption) return true
											return (
												foundOption.email.toLowerCase().includes(formattedKey) ||
												foundOption.fullName.toLowerCase().includes(formattedKey)
											)
										}}
									>
										{departmentMembers.map(({ id, fullName, email, access }) => {
											const title = `${fullName} (${email})`

											return (
												<Select.Option key={email} value={id!}>
													<Styled.UsersGrid>
														<Typography.Text ellipsis title={title}>
															{title}
														</Typography.Text>
														<Container display="flex" justifyContent="flex-end">
															{access === UserAccess.Admin && (
																<Styled.RoleTag>{t('common.terms.admin')}</Styled.RoleTag>
															)}
														</Container>
													</Styled.UsersGrid>
												</Select.Option>
											)
										})}
									</Select>
								</FormItem>
							</UserInteraction>
							<Styled.ContinueButton
								disabled={!dirty || !isValid}
								loading={isLoading}
								title="Continue"
								type="primary"
								shape="round"
							>
								{t('auth.pages.activeDirectory.submit')}
							</Styled.ContinueButton>
						</Form>
					)}
				</Formik>
				<Container
					margin={{ top: 'xxl' }}
					display={'flex'}
					flexDirection={'column'}
					justifyContent={'flex-end'}
					alignItems={'center'}
				>
					<Button type="link" onClick={() => logout(true)}>
						{t('auth.pages.activeDirectory.logout')}
					</Button>
				</Container>
			</Col>
		</Row>
	)
}

export default ActiveDirectoryPageContent
